import type { UseTodosOptions } from "../types/todos";

/**
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ SAVED SMART VIEWS — filter model                                        │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * A saved view is a named set of filters that the user
 * persists (`saved_view` table, synchronized via PowerSync, personal scope).
 * Filters are stored as JSON with the SAME shape as `UseTodosOptions`
 * so that the view is literally "a named `getTodosQuery`".
 *
 * JSON travels client->server as opaque text (server only validates that
 * it is well-formed JSON); that is why the parse back is TOLERANT: unknown
 * keys or incorrect types are discarded instead of breaking the view.
 */
export interface SavedViewFilters {
  /** Only important tasks. */
  importantOnly?: boolean;
  /** Only tasks with due date ("Planned" view). */
  plannedOnly?: boolean;
  /** Only "My Day" tasks (my_day_date <= today). */
  myDayOnly?: boolean;
  /** Restrict to a specific list (`null` = inbox, absent = all). */
  listId?: string | null;
  /** Substring to search in the task title. */
  searchQuery?: string;
}

/** Recognized boolean keys of filters JSON. */
const BOOL_KEYS = ["importantOnly", "plannedOnly", "myDayOnly"] as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * TOLERANT parse of JSON persisted in `saved_view.filters`: values with
 * incorrect type or unknown keys are discarded silently (a
 * view with corrupt filters degrades to "all tasks", never crashes).
 */
export function parseSavedViewFilters(raw: string | null | undefined): SavedViewFilters {
  if (!raw) {
    return {};
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!isPlainObject(parsed)) {
    return {};
  }
  const source = parsed;
  const filters: SavedViewFilters = {};
  for (const key of BOOL_KEYS) {
    const value = source[key];
    if (typeof value === "boolean") {
      filters[key] = value;
    }
  }
  const listIdRaw = source.listId;
  if (typeof listIdRaw === "string" || listIdRaw === null) {
    filters.listId = listIdRaw;
  }
  if (typeof source.searchQuery === "string" && source.searchQuery.trim() !== "") {
    filters.searchQuery = source.searchQuery;
  }
  return filters;
}

/**
 * Serializes filters for persistence: omits keys without effect (false /
 * empty string / undefined) so saved JSON is minimal and stable.
 */
export function serializeSavedViewFilters(filters: SavedViewFilters): string {
  const out: Record<string, unknown> = {};
  for (const key of BOOL_KEYS) {
    if (filters[key] === true) {
      out[key] = true;
    }
  }
  if (filters.listId !== undefined) {
    out.listId = filters.listId;
  }
  if (filters.searchQuery !== undefined && filters.searchQuery.trim() !== "") {
    out.searchQuery = filters.searchQuery.trim();
  }
  return JSON.stringify(out);
}

/**
 * Translates saved view filters to canonical builder options
 * of `getTodosQuery` — the view IS a persisted `UseTodosOptions`.
 */
export function savedViewFiltersToTodosOptions(filters: SavedViewFilters): UseTodosOptions {
  const options: UseTodosOptions = {};
  if (filters.importantOnly) {
    options.importantOnly = true;
  }
  if (filters.plannedOnly) {
    options.plannedOnly = true;
  }
  if (filters.myDayOnly) {
    options.myDayOnly = true;
  }
  if (filters.listId !== undefined) {
    options.listId = filters.listId;
  }
  if (filters.searchQuery) {
    options.searchQuery = filters.searchQuery;
  }
  return options;
}

/** Is there any effective filter? (view without filters = "all tasks") */
export function hasActiveFilters(filters: SavedViewFilters): boolean {
  return (
    BOOL_KEYS.some((key) => filters[key] === true) ||
    filters.listId !== undefined ||
    Boolean(filters.searchQuery)
  );
}
